// Checks for files people choose: an audio recording made elsewhere ("Upload a recording
// instead") and the optional photo. Plain-English messages; nothing here logs file names.
import { limits } from '../lib/config.js';
import { audioFileType, imageFileType } from './mime.js';

const MB = 1048576;
export const maxBytes = () => limits.maxFileBytes || 100 * MB;
export const maxClipSeconds = () => limits.maxClipSeconds || 900;
const mbLabel = () => `${Math.round(maxBytes() / MB)} MB`;
const minutesLabel = () => `${Math.round(maxClipSeconds() / 60)} minutes`;

/**
 * Read an audio file's length with an <audio> element. Resolves { seconds, playable }.
 * seconds is null when the browser cannot tell (WebM files from MediaRecorder report Infinity
 * until scanned; we nudge them with a far seek, then give up after a few seconds).
 */
export function probeDuration(blob, { timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.muted = true;
    const url = URL.createObjectURL(blob);
    const finish = (seconds, playable) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      audio.removeAttribute('src');
      try { audio.load(); } catch { /* ignore */ }
      URL.revokeObjectURL(url);
      resolve({ seconds: seconds != null && isFinite(seconds) && seconds > 0 ? seconds : null, playable });
    };
    const timer = setTimeout(() => finish(isFinite(audio.duration) ? audio.duration : null, audio.readyState > 0), timeoutMs);
    let nudged = false;
    const check = () => {
      const d = audio.duration;
      if (isFinite(d) && d > 0) { finish(d, true); return; }
      if (!nudged && (d === Infinity || Number.isNaN(d))) {
        nudged = true;
        try { audio.currentTime = 1e101; } catch { finish(null, true); }
      }
    };
    audio.addEventListener('loadedmetadata', check);
    audio.addEventListener('durationchange', check);
    audio.addEventListener('timeupdate', check);
    audio.addEventListener('error', () => finish(null, false));
    audio.src = url;
  });
}

/**
 * Check an audio file. Resolves { ok: true, type, ext, durationSec, playable } or { ok: false, message }.
 * durationSec is what we know, never above the limit; 0 when the length cannot be read.
 */
export async function checkAudioFile(file) {
  if (!file) return { ok: false, message: 'Choose a recording first.' };
  const t = audioFileType(file);
  if (!t) return { ok: false, message: 'That file is not a sound recording we can use. Choose an audio file, such as one ending in .m4a, .mp3, .wav, or .webm.' };
  if (!file.size) return { ok: false, message: 'That file is empty. Choose another recording.' };
  if (file.size > maxBytes()) return { ok: false, message: `That file is too big. The limit is ${mbLabel()}. Choose a shorter recording, or email it to us instead.` };
  const { seconds, playable } = await probeDuration(file);
  if (seconds != null && seconds > maxClipSeconds() + 1) {
    return { ok: false, message: `That recording is longer than ${minutesLabel()}. Please choose a shorter one, or split it into parts.` };
  }
  return { ok: true, type: t.type, ext: t.ext, durationSec: seconds != null ? Math.min(seconds, maxClipSeconds()) : 0, playable };
}

/** Check a photo. Returns { ok: true, type, ext } or { ok: false, message }. */
export function checkImageFile(file) {
  if (!file) return { ok: false, message: 'Choose a photo first.' };
  const t = imageFileType(file);
  if (!t) return { ok: false, message: 'That file is not a photo we can use. Choose a JPEG, PNG, HEIC, WebP, or GIF picture.' };
  if (!file.size) return { ok: false, message: 'That photo file is empty. Choose another one.' };
  if (file.size > maxBytes()) return { ok: false, message: `That photo is too big. The limit is ${mbLabel()}.` };
  return { ok: true, type: t.type, ext: t.ext };
}

/** A stable fingerprint for "is this the same photo we already uploaded?" (never logged). */
export function fileSignature(file) {
  return file ? `${file.size}:${file.lastModified || 0}:${file.type || ''}:${(file.name || '').length}` : null;
}
